import { SelectProps, TextField as MuiTextField } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField';
import cx from 'classnames';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { ErrorMessage } from 'src/components/ErrorMessage';
import { ChevronDownIcon } from 'src/components/icons';
import { GraySmall } from 'src/theme/colors';

export type TextFieldSizeVariant = 'skinny' | 'medium' | 'standard' | 'tall';

const useStyles = makeStyles()((theme) => ({
  placeholder: {
    '& .MuiOutlinedInput-input': {
      color: GraySmall,
    },
  },
  skinny: {
    '& .MuiOutlinedInput-input': {
      '&:not(textarea)': {
        height: '30px',
      },
    },
  },
  medium: {
    '& .MuiOutlinedInput-input': {
      '&:not(textarea)': {
        height: '36px',
      },
    },
  },
  standard: {
    '& .MuiOutlinedInput-input': {
      '&:not(textarea)': {
        height: '38px',
      },
    },
  },
  tall: {
    '& .MuiOutlinedInput-input': {
      '&:not(textarea)': {
        height: '48px',
      },
    },
  },
  dropdownIcon: {
    height: '15px',
    width: '15px',
    color: GraySmall,
    marginRight: theme.spacing(0.5),
    top: theme.spacing(1.5),
  },
}));

export type BaseTextFieldProps = TextFieldProps & {
  sizeVariant?: TextFieldSizeVariant;
  isPlaceholder?: boolean;
  additionalSelectProps?: Partial<SelectProps>;
};

export const TextField: React.FC<BaseTextFieldProps> = ({
  sizeVariant = 'skinny',
  additionalSelectProps,
  onBlur,
  isPlaceholder,
  onChange,
  className,
  helperText,
  ...textFieldProps
}) => {
  const { classes } = useStyles();
  const classNameOptions = React.useMemo(() => {
    const options = {
      [classes.placeholder]: isPlaceholder,
    };
    if (className) {
      options[className] = true;
    }
    return options;
  }, [className, isPlaceholder]);
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const stagedEvent = event;
    // For cases where value may not be simply string, we do not want to call onChange event
    if (
      event.target.value &&
      typeof event.target.value === 'string' &&
      !textFieldProps.select && // No trimming required for dropdowns
      !textFieldProps?.inputProps?.isautocomplete // No trimming for auto-complete controls
    ) {
      stagedEvent.target.value = event.target.value.trim();
      onChange?.(stagedEvent);
    }
    // onBlur to be called regardless
    onBlur?.(stagedEvent);
  };

  return (
    <MuiTextField
      SelectProps={{
        classes: { icon: classes.dropdownIcon },
        IconComponent: ChevronDownIcon,
        className: 'bg-red',
        ...additionalSelectProps,
      }}
      onBlur={handleBlur}
      onChange={onChange}
      classes={{
        root: classes[sizeVariant],
      }}
      className={cx(classNameOptions)}
      InputProps={{
        ...textFieldProps.InputProps,
        classes: {
          ...textFieldProps.InputProps?.classes,
          root: '[&.Mui-error_fieldset]:!border-error-primary',
        },
      }}
      {...textFieldProps}
      helperText={
        textFieldProps.error &&
        typeof helperText === 'string' &&
        helperText.trim().length > 0 ? (
          <ErrorMessage message={helperText} />
        ) : (
          helperText
        )
      }
    />
  );
};
